import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useSpring } from "framer-motion";
import { company } from "@/data/site";
import { FadeUp, Lines } from "@/components/motion/Reveal";
import { TerminDialog } from "@/components/TerminDialog";

export const Hero = () => {
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 80, damping: 24, mass: 0.4 });
  // Dezente Parallaxe über MotionValues – rendert den Startwert sofort, bleibt also auch ohne Animation sichtbar
  const carY = useTransform(smooth, [0, 1], reduce ? ["0%", "0%"] : ["0%", "14%"]);
  const carScale = useTransform(smooth, [0, 1], reduce ? [1, 1] : [1, 1.12]);

  return (
    <section ref={sectionRef} data-testid="hero-section" className="relative min-h-[88vh] flex items-center pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden">
      {/* Atmosphärisches Auto – immer sichtbar (statische Opazität), nur die Parallaxe ist dynamisch */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-full md:w-[72%]">
        <motion.img
          src="/hero-car.jpg"
          alt=""
          className="h-full w-full object-cover object-right will-change-transform"
          style={{
            y: carY,
            scale: carScale,
            opacity: 0.85,
            filter: "blur(1.5px) saturate(1.1)",
            mixBlendMode: "screen",
            maskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 32%, #000 70%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 32%, #000 70%)",
          }}
        />
      </div>

      {/* Violetter Glow für Tiefe */}
      <div aria-hidden="true" className="pointer-events-none absolute top-[12%] right-[6%] h-[460px] w-[460px] rounded-full bg-[color:var(--plum)] opacity-[0.20] blur-[170px]" />
      {/* Dunkle Verläufe: links voll schwarz für den Text, unten sanfter Übergang */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[color:var(--bg)] via-[color:var(--bg)]/70 to-transparent" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[color:var(--bg)] to-transparent" />

      <div className="container-x relative">
        <div className="max-w-3xl">
          <FadeUp as="p" delay={0.1} className="eyebrow mb-6">{company.claim}</FadeUp>
          <Lines as="h1" lines={["Willkommen bei der", "Nivo Garage!"]} className="h-display" delay={0.15} data-testid="hero-headline" />
          <FadeUp delay={0.45} className="lead mt-7 max-w-xl">
            Ihre Garage für alle Automarken – persönlich, ehrlich und zuverlässig.
          </FadeUp>
          <FadeUp delay={0.55} className="mt-6 max-w-xl space-y-4 text-[color:var(--ink-2)] leading-relaxed text-[16px] md:text-[17px]">
            <p>Ich bin Nikola Mirkovic und kümmere mich mit Leidenschaft und Fachwissen um Ihr Fahrzeug.</p>
            <p>Ob Service, Reparatur oder eine schwierige Fehlersuche – bei mir erhalten Sie ehrliche Beratung, saubere Arbeit und einen persönlichen Service, auf den Sie sich verlassen können.</p>
            <p>Denn für mich zählen nicht nur die Autos, sondern auch die Menschen dahinter.</p>
          </FadeUp>
          <FadeUp delay={0.65} className="mt-9 flex flex-wrap gap-3">
            <TerminDialog testId="hero-appointment-button" className="btn-violet" />
            <Link to="/leistungen" data-testid="hero-services-button" className="btn-outline">Unsere Leistungen</Link>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
